// Lib
import React, { CSSProperties, RefObject } from 'react';
import classNames from 'classnames';

// Utils
import {
    getBackgroundColor,
    getCaption,
    getElementId,
    getImageThumbnail,
    getIsTransparentImage,
    getShowCaption,
} from '../../../../../../common/elements/utils/elementPropertyUtils';
import { toCssUrl } from '../../../../../utils/cssUtil';
import { getColorCssValue } from '../../../../utils/elementColorStyleUtils';

// Components
import Caption from '../../../../../components/caption/Caption';
import ElementImage from '../../../../../components/images/ElementImage';
import ImageDrawing from '../../../ImageDrawing';

// Hooks
import { useImageModalV2ZoomInteractions } from './hooks/useImageModalV2ZoomInteractions';

// Constants
import { IMAGE_TYPES } from '../../../../../../common/media/mediaConstants';

import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { UseImageModalZoomResult } from '../../hooks/useImageModalZoom';

type ImageModalV2ContentProps = {
    element: ImMNElement;
    lowerQualityImageUrl?: string;
    isEditable: boolean;
    showTransparencyGrid: boolean;
    isHiddenForEdit?: boolean;
    contentStyle: CSSProperties;
    imageWrapperStyle: CSSProperties;
    captionRef: RefObject<HTMLDivElement>;
    imageZoom: UseImageModalZoomResult;
};

/**
 * Renders the image filling the available space while maintaining aspect ratio.
 */
const ImageModalV2Content: React.FC<ImageModalV2ContentProps> = ({
    element,
    lowerQualityImageUrl,
    isEditable,
    showTransparencyGrid,
    isHiddenForEdit,
    contentStyle,
    imageWrapperStyle,
    captionRef,
    imageZoom,
}) => {
    const elementId = getElementId(element);
    const isTransparent = getIsTransparentImage(element);
    const backgroundColorCss = getColorCssValue(getBackgroundColor(element));
    const imageThumbnailUrl = getImageThumbnail(element);
    const showCaption = getShowCaption(element);
    const { isPanning, isZoomed } = imageZoom;
    const imageBackgroundStyle =
        !isTransparent && imageThumbnailUrl ? { backgroundImage: toCssUrl(imageThumbnailUrl) } : undefined;
    const imageWidthPx = Number(imageWrapperStyle.width);
    const imageHeightPx = Number(imageWrapperStyle.height);
    const shouldShowTransparencyGrid =
        showTransparencyGrid && isTransparent && !backgroundColorCss && imageWidthPx > 0 && imageHeightPx > 0;
    const { zoomViewportRef, onImageMouseDown, zoomTransformStyle } = useImageModalV2ZoomInteractions(imageZoom);

    return (
        <div
            ref={zoomViewportRef}
            className={classNames('ImageModalV2Content flex h-full w-full items-center justify-center', {
                'is-zoomed': isZoomed,
                'is-panning': isPanning,
                'is-hidden-for-edit': isHiddenForEdit,
            })}
            onMouseDown={onImageMouseDown}
        >
            <div
                className="ImageModalV2Content-frame"
                style={{
                    ...contentStyle,
                    ...(backgroundColorCss && { '--ws-element-background-primary': backgroundColorCss }),
                }}
            >
                <div
                    className="ImageModalV2Content-image-wrapper"
                    style={{
                        ...imageWrapperStyle,
                        ...zoomTransformStyle,
                    }}
                >
                    <div
                        className={classNames('ImageModalV2Content-image', {
                            'cropper-bg': shouldShowTransparencyGrid,
                        })}
                        style={{
                            ...imageBackgroundStyle,
                            ...imageWrapperStyle,
                        }}
                    >
                        <ElementImage
                            useSecureMediaUrl
                            showBrokenIconOnError
                            element={element}
                            imageType={IMAGE_TYPES.ELEMENT}
                            widthPx={imageWidthPx}
                            thumbnailUrl={lowerQualityImageUrl}
                            displayFullSize
                            key={elementId}
                        />
                        <ImageDrawing element={element} />
                    </div>
                </div>
                {showCaption && (
                    <div
                        ref={captionRef}
                        className={classNames('ImageModalV2Content-caption flex-none', {
                            'is-hidden': isZoomed,
                        })}
                    >
                        <Caption
                            element={element}
                            textContent={getCaption(element)}
                            captionVisible={showCaption}
                            isEditable={isEditable}
                            isModalView
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageModalV2Content;
